import { CollapseProps, Flex, message, Select } from "antd";
import Search from "antd/es/input/Search";
import dayjs from "dayjs";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
    getCatalogFinGroupsDetailThunk,
    getCatalogFinGroupsThunk,
    getExportExportPromosThunk,
    getPromoActual,
} from "tm-shared/api";
import { getStorageInventoryGetThunk } from "tm-shared/api";
import {
    PaymentForm,
    ProductOrderItem,
    PromoDetails,
    TaskSaleAdditional,
} from "tm-shared/types";
import { countText } from "tm-shared/utils";

import BaseButton from "@/components/BaseButton";
import FlexContainer from "@/components/FlexContainer";
import Header from "@/components/Header";
import { FixedCenteredLayout, MainContainer } from "@/components/Layout/Styled";
import { Loading } from "@/components/Loading";
import Meta from "@/components/Meta";
import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { BlockLoader } from "@/components/UI/BlockLoader";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import ExportDropdown from "@/pages/SV/RouteManagement/components/ExportDropdown";
import { useCreateCheckout } from "@/pages/WorkPlan/Tasks/hooks";
import { setSalePaymentForm, setSaleProduct } from "@/store/slices/progress";
import { setSaleQuery } from "@/store/slices/ui";

import { GeneralProductCard } from "../components/GeneralProductCard";
import {
    ButtonsWrapper,
    SearchWrapper,
    StyledFormWarning,
    StyledTypographyFormWarning,
} from "../shared";
import { StyledAccordionHeader, StyledPromoCollapse } from "./Styled";

const formList = [
    {
        value: PaymentForm.Cash1,
        label: "Form 1",
    },
    {
        value: PaymentForm.Card2,
        label: "Form 2",
    },
];

const promoType = {
    p: "Цінова акція",
    d: "Цінова акція",
    bl: "Кількісна пляшкова акція",
    bs: "Кількісна пляшкова акція",
    m: "Гривнева акція",
};

interface PromoCatalogPageProps {
    marketId: string;
    task: TaskSaleAdditional;
}

export const PromoCatalogPage: FC<PromoCatalogPageProps> = ({
    marketId,
    task,
}) => {
    const dispatch = useAppDispatch();

    const [formTouched, setFormTouched] = useState(false);
    const [promoLoading, setPromoLoading] = useState(false);
    const [actualPromo, setActualPromo] = useState<PromoDetails[]>([]);
    const [messageApi, contextHolder] = message.useMessage();

    const { saleAdditional } = useAppSelector((state) => state.progress);

    const { query: searchQuery } = useAppSelector(
        (state) => state.ui.saleAdditional,
    );

    const { markets, exportIndicator } = useAppSelector((state) => state.work);

    const taskId = task.id;

    const currentPayload = saleAdditional[taskId];
    const currentProducts = useMemo(
        () => currentPayload?.products ?? {},
        [currentPayload?.products],
    );
    const currentPaymentForm = currentPayload?.paymentForm;
    const currentIgnoreRemainings = currentPayload?.ignoreRemainings ?? [];

    const {
        offlineMode,
        promoList,
        products,
        marketsPriceLists,
        priceLists,
        inventoryList,
        promosVocabulary,
    } = useAppSelector((state) => state.offline);

    const currentMarket = useMemo(
        () => markets?.[marketId],
        [markets, marketId],
    );

    const currentPriceList = useMemo(() => {
        const priceListId = marketsPriceLists?.[marketId];
        return priceLists?.[priceListId] ?? {};
    }, [marketId, marketsPriceLists, priceLists]);

    const storageId = currentMarket?.storage?.id ?? "";
    const currentInventoryList = inventoryList?.[storageId] || {};

    const promoCatalog = useMemo(() => {
        if (!offlineMode) {
            return actualPromo;
        }

        const marketPromoKey =
            promosVocabulary?.[currentMarket?.id] ??
            promosVocabulary?.[currentMarket?.partner?.id];

        return promoList?.[marketPromoKey];
    }, [
        offlineMode,
        actualPromo,
        promoList,
        promosVocabulary,
        currentMarket?.id,
        currentMarket?.partner?.id,
    ]);

    const { handle: handleSendProductsToCheckout, isLoading } =
        useCreateCheckout({
            task,
            marketId,
            paymentForm: currentPaymentForm,
            products: currentProducts,
            shouldIgnoreRemainings: currentIgnoreRemainings.length > 0,
        });

    const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setSaleQuery(e.target.value));
    };

    useEffect(() => {
        if (Object.keys(markets)?.length && !offlineMode) {
            dispatch(
                getStorageInventoryGetThunk({
                    storageId: markets[marketId]?.storage?.id,
                }),
            );
            dispatch(getCatalogFinGroupsThunk());
            dispatch(getCatalogFinGroupsDetailThunk());
        }
    }, [markets, dispatch, offlineMode, marketId]);

    useEffect(() => {
        if (!offlineMode) {
            const fetchPromo = async () => {
                try {
                    setPromoLoading(true);

                    const { result } = await getPromoActual({ marketId });

                    setActualPromo(result);
                } catch (error) {
                    console.error(error);
                } finally {
                    setPromoLoading(false);
                }
            };

            fetchPromo();
        }
    }, [dispatch, marketId, offlineMode]);

    const handleProductCountChange = useCallback(
        (product: ProductOrderItem) => {
            dispatch(
                setSaleProduct({
                    taskId,
                    product,
                }),
            );
        },
        [dispatch, taskId],
    );

    const filteredPromoCatalog = useMemo(() => {
        if (!promoCatalog) return [];

        if (!searchQuery) return promoCatalog;

        const query = searchQuery.toLowerCase();

        return promoCatalog.filter((promo) => {
            const typeName = promoType?.[promo?.type]?.toLowerCase() || "";
            const description = promo?.description?.toLowerCase() || "";

            return typeName.includes(query) || description.includes(query);
        });
    }, [promoCatalog, searchQuery]);

    const items: CollapseProps["items"] = filteredPromoCatalog?.map((item) => {
        const totalStockBalance = Object.values(
            item?.context?.stockBalances,
        ).reduce((acc, el) => acc + el, 0);

        const promoProducts = Object.entries(item?.context?.stockBalances).map(
            ([uniqueCode, stockBalance]) => ({
                name:
                    products[item?.context?.presentSKU?.[uniqueCode]]?.name ||
                    "-",
                stockBalance,
            }),
        );

        const knownProducts = Object.values(item?.context?.SKU || []).reduce(
            (acc, sku) => {
                const product = products[sku];
                if (product?.name) acc.push(sku);
                return acc;
            },
            [],
        );

        return {
            key: item?.id,
            label: (
                <StyledAccordionHeader
                    isZeroPresents={totalStockBalance === 0}
                    isExternal={item?.isExternal}
                >
                    <FlexContainer flexDirection="column">
                        <FlexContainer
                            flexDirection="column"
                            alignItems="flex-start"
                            justifyContent="flex-start"
                            width="100%"
                            style={{ marginBottom: "10px" }}
                        >
                            <PrimaryText>
                                {`${promoType?.[item?.type]} до ${dayjs(
                                    item?.endDate,
                                ).format("YYYY-MM-DD")}`}{" "}
                            </PrimaryText>
                            <PrimaryText style={{ textWrap: "nowrap" }}>
                                {item?.context?.SKU?.length === 0
                                    ? "В акції беруть участь всі товари"
                                    : `В акції беруть участь ${countText.good(
                                          knownProducts.length,
                                      )}`}
                            </PrimaryText>
                        </FlexContainer>
                        <SecondaryText>{item?.description}</SecondaryText>
                    </FlexContainer>

                    {totalStockBalance > 0 ? (
                        <Flex vertical gap={10}>
                            <PrimaryText fontWeight="600">
                                Залишок подарунків:
                            </PrimaryText>

                            <Flex vertical gap={4}>
                                {promoProducts.map((item, index) => (
                                    <SecondaryText key={index}>
                                        {item?.name}: {item?.stockBalance}
                                    </SecondaryText>
                                ))}
                            </Flex>
                        </Flex>
                    ) : (
                        <FlexContainer>
                            <PrimaryText $textColor="error" fontWeight="600">
                                Подарунки закінчилися. Акція не спрацює!
                            </PrimaryText>
                        </FlexContainer>
                    )}
                </StyledAccordionHeader>
            ),
            children: (
                <Flex vertical>
                    {Object.values(item?.context?.SKU).length === 0 ? (
                        <PrimaryText textAlign="center">
                            В акції беруть участь всі продукти. Будь ласка,
                            поверніться в продаж та оберіть потрібні товари
                        </PrimaryText>
                    ) : (
                        knownProducts.map((barcode) => {
                            const currentCount =
                                currentProducts[barcode]?.count ?? 0;
                            const isProductRemainingIgnored =
                                currentIgnoreRemainings.includes(barcode);

                            return (
                                <GeneralProductCard
                                    key={barcode}
                                    {...products?.[barcode]}
                                    marketId={marketId}
                                    price={currentPriceList?.[barcode]?.price}
                                    oldPrice={
                                        currentPriceList?.[barcode]?.oldPrice
                                    }
                                    hasSelectedPaymentForm={
                                        !!currentPaymentForm
                                    }
                                    itemsLeft={currentInventoryList?.[barcode]}
                                    value={currentCount}
                                    taskId={taskId}
                                    onCountChange={handleProductCountChange}
                                    isProductRemainingIgnored={
                                        isProductRemainingIgnored
                                    }
                                    isRemainingIgnored={
                                        currentIgnoreRemainings.length > 0
                                    }
                                />
                            );
                        })
                    )}
                </Flex>
            ),
        };
    });

    const handleExportPromos = useCallback(
        async ({ key }: { key: string }) => {
            switch (key) {
                case "excel":
                    dispatch(
                        getExportExportPromosThunk({
                            partnerId: currentMarket?.partner?.id,
                        }),
                    );
                    break;
            }

            messageApi.info(`Експорт усіх акцій партнера розпочато`);
        },
        [currentMarket?.partner?.id, dispatch, messageApi],
    );

    return (
        <>
            <Meta title="Акційні пропозиції" />
            <Header
                title="Акційні пропозиції"
                back
                rightActions={
                    <ExportDropdown
                        isLoading={!!exportIndicator}
                        loadingLabel={exportIndicator.toString()}
                        menuItems={[{ key: "excel", label: "Експорт в Excel" }]}
                        onMenuClick={handleExportPromos}
                    />
                }
            />
            {contextHolder}
            {formTouched && (
                <StyledFormWarning>
                    <StyledTypographyFormWarning>
                        Увага! Ви змінили форму оплати, продаж буде тільки по
                        ній
                    </StyledTypographyFormWarning>
                </StyledFormWarning>
            )}
            <SearchWrapper>
                <div>
                    <Search
                        placeholder="Пошук"
                        defaultValue={searchQuery}
                        onChange={onSearchChange}
                        allowClear
                        size="large"
                        style={{
                            marginRight: "6px",
                        }}
                    />
                </div>
                <Select
                    options={formList}
                    value={currentPaymentForm}
                    disabled={promoLoading}
                    onChange={(value: PaymentForm) => {
                        if (!formTouched && currentPaymentForm)
                            setFormTouched(true);
                        dispatch(
                            setSalePaymentForm({
                                taskId,
                                paymentForm: value,
                            }),
                        );
                    }}
                    placeholder="Форма"
                    size="large"
                />
            </SearchWrapper>
            <MainContainer>
                {promoLoading && <BlockLoader text={"Завантаження акцій..."} />}

                {!promoLoading &&
                    (items?.length === 0 ? (
                        <PrimaryText>Акцій не знайдено</PrimaryText>
                    ) : (
                        <>
                            <StyledPromoCollapse
                                expandIconPosition="start"
                                items={items}
                            />

                            <ButtonsWrapper>
                                {Object.values(currentProducts)?.length > 0 && (
                                    <FixedCenteredLayout
                                        bottom="5px"
                                        padding="0 10px"
                                    >
                                        <BaseButton
                                            type="primary"
                                            loading={isLoading}
                                            disabled={isLoading}
                                            onClick={
                                                handleSendProductsToCheckout
                                            }
                                        >
                                            Кошик
                                        </BaseButton>
                                    </FixedCenteredLayout>
                                )}
                            </ButtonsWrapper>
                        </>
                    ))}
            </MainContainer>

            {isLoading && <Loading title="Завершення задачі" />}
        </>
    );
};
